import * as diacritics from "diacritics"
import { DeliveryProvider, Restaurant } from "../models/restaurant"
import moment from "moment"
import { AdditionGroup } from "../models/additionGroup"
import { Term } from "../models/signup-info-item"
import { translationKeys } from "./translationKeys"
import { TFunction } from "i18next"

export const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

export const sanitizeString = (string: string) => {
  return diacritics.remove(string.toLowerCase().replace(/\s/g, ""))
}

export const isBuzzRiders = (provider: DeliveryProvider | null) => {
  const buzzBrands = ["ritta", "otto", "jagger", "vendor1-staging"] // Add "localhost" to the list if you want to test locally
  const isBuzz = isCustomer(buzzBrands)

  return provider === "rider" && isBuzz
}

const isCustomer = (brands: string[]) => {
  if (typeof window !== "undefined") {
    const pathname = window.location.hostname
    return brands.some((brand) => pathname.includes(brand))
  }

  return false
}

export const getDeliveryText = (
  t: TFunction,
  provider: DeliveryProvider | null
) => {
  if (isBuzzRiders(provider)) {
    return "Buzz riders"
  }

  if (isCustomer(["stykke", "localhost"])) {
    return t(translationKeys.stykkeDeliveryByWolt)
  }

  return t(translationKeys.venuedetailsDeliveryOptionsDelivery)
}

export const setHTMLTagLang = (lang: string) => {
  document.documentElement.setAttribute("lang", lang)
}

// Determines from the client key if we are in live env or in test env
export const getAdyenEnvironment = (key: string): "live" | "test" => {
  const isLive = key.startsWith("live")
  if (isLive) {
    return "live"
  }
  return "test"
}

export const getShowBenefitsCheckoutSelectorAndRedeemCode = (
  accountType: "guest" | "user"
) => {
  return accountType === "user"
}

export const getShowGiftCardSection = (
  loyaltyMode: Restaurant["shop"]["loyalty_mode"],
  accountType: "guest" | "user"
) => {
  return loyaltyMode === "enabled_all" && accountType === "guest"
}

export const getIsBenefitsEnabled = (
  shop: Restaurant["shop"],
  accountType: "guest" | "user"
) => {
  const loyaltyMode = shop.loyalty_mode
  const showBenefitsCheckoutSelectorAndRedeemCode =
    getShowBenefitsCheckoutSelectorAndRedeemCode(accountType)
  const showGiftCardSection = getShowGiftCardSection(
    shop.loyalty_mode,
    accountType
  )

  // If loyalty is disabled, we don't show benefits
  // Or if we are not supposed to show any of them.
  if (
    loyaltyMode === "disabled" ||
    (!showBenefitsCheckoutSelectorAndRedeemCode && !showGiftCardSection)
  ) {
    return false
  }

  // Otherwise, we show benefits. CheckoutBenefitsSection will handle the logic for guest and user.
  return true
}

export const baseUrl = process.env.GATSBY_PAGE_BASE_URL

export const pensopayStatus = "pensopayStatus"
export const pensopayFailed = "failed"
export const pensopaySuccess = "success"
export type PensopayStatus = typeof pensopaySuccess | typeof pensopayFailed

// Pathnames comes with <language>/ prefix. We need to remove it.
export const getNavigatingFrom = (pathname: string) => {
  const splittedPathname = pathname.split("/").filter((p) => p !== "")
  if (splittedPathname.length === 0) {
    return "/"
  }
  return `/${splittedPathname.slice(1, splittedPathname.length).join("/")}`
}

export const formatDateWithTime = (date: string) => {
  return moment(date).format("MMM DD, HH:mm")
}

export const findAdditionGroupsWithMissingSharedPriceSelections = (
  additionGroups: AdditionGroup[],
  quantityToEvaluateMaxMinWith: number
) => {
  const additionGroupsWithSharedPriceEnabled = additionGroups.filter(
    (additionGroup) => {
      return !!additionGroup.shared_option_selection
    }
  )
  const additionGroupsWithMissingSharedPriceSelections =
    additionGroupsWithSharedPriceEnabled.filter((additionGroup) => {
      const additionGroupSelectionsCount = additionGroup.additions.reduce(
        (acc, next) => {
          return acc + next.count
        },
        0
      )
      return (
        additionGroupSelectionsCount <
        (additionGroup?.shared_option_selection?.limit || 0) *
          quantityToEvaluateMaxMinWith
      )
    })
  return additionGroupsWithMissingSharedPriceSelections
}

export const getTermLabel = (term: Term, t: TFunction) => {
  let label: string = term.disclaimer
  if (term.key_name.includes("privacy")) {
    label = t(translationKeys.comboMembershipTermsReadAndAccept)
  } else if (term.key_name.includes("marketing")) {
    label = t(translationKeys.comboMembershipTermsMarketing)
  }
  return label
}

// https://github.com/Adyen/adyen-web/tree/main/packages/server/translations
export const adyenLocales = new Map([
  ["da", "da-DK"],
  ["en", "en-US"],
  ["no", "no-NO"],
  ["is", "is-IS"],
  ["sv", "sv-SE"],
  ["fr", "fr-FR"],
  ["de", "de-DE"],
  ["nl", "nl-NL"],
])

// Used for fallback country code, if not present in adyen next action object.
// Converts url to to letter code.
const countryCodes = new Map([
  ["nor", "NO"],
  ["isl", "IS"],
  ["swe", "SE"],
  ["dnk", "DK"],
])

export const getAdyenLocale = (language: string) => {
  return adyenLocales.get(language) || "da-DK"
}

export const getAdyenCountryCode = (country: string) => {
  if (country.length === 2) {
    return country.toUpperCase()
  }

  return countryCodes.get(country) || "DK"
}
